import {
  isValidStateWasteCode,
  isValidStateWasteCodeInAnyState,
  isValidTxWasteCodeFormat,
  STATE_WASTE_CODES,
} from "./stateWasteCodes";

/**
 * Matches a two-letter state prefix followed by a dash and a non-empty suffix,
 * e.g. "TX-21902191" or "IL-06". Case-insensitive on the prefix.
 */
const STATE_PREFIX_REGEX = /^([A-Za-z]{2})-(.+)$/;

/**
 * Matches a two-letter state prefix immediately followed by a non-empty suffix
 * with no separator, e.g. "ORX001". Case-insensitive on the prefix. Used as a
 * secondary, allow-list-gated strip path for the no-dash form.
 */
const NO_DASH_STATE_PREFIX_REGEX = /^([A-Za-z]{2})(.+)$/;

/**
 * Normalize a single state waste code by stripping known state prefixes.
 *
 * Rules:
 * - null/undefined/empty/whitespace → null
 * - Trim whitespace from the input
 * - If the trimmed input matches `<XX>-<suffix>` (case-insensitive prefix):
 *   - When the prefix is `TX`, always return the suffix (Texas codes are
 *     facility-specific and the `TX-` marker is added at ingestion time).
 *   - Otherwise, return the suffix only if `isValidStateWasteCode(suffix, prefix)`
 *     would accept it; if not, return the trimmed original (so we don't mangle
 *     codes that happen to look prefix-shaped but aren't real state codes).
 * - If there's no dash but the trimmed input is `<XX><suffix>` where `XX` is a
 *   state with a canonical allow-list and `<suffix>` validates against it
 *   (e.g. `ORX001` → `X001`), return the suffix. Facility-specific states (TX)
 *   are intentionally skipped here because every input would validate and the
 *   leading two characters might be part of a real facility code.
 * - Inputs without a recognizable prefix are returned trimmed but otherwise
 *   unchanged.
 */
export function normalizeStateWasteCode(code: string | null | undefined): string | null {
  if (code === null || code === undefined) {
    return null;
  }

  const trimmed = code.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const dashMatch = trimmed.match(STATE_PREFIX_REGEX);
  if (dashMatch) {
    // Trim the captured suffix so inputs like "TX- W102" (space after the
    // dash) canonicalize to "W102" rather than " W102". The outer trim above
    // only handles leading/trailing whitespace on the whole input.
    const [, rawPrefix, rawSuffix] = dashMatch;
    const suffix = rawSuffix.trim();
    const prefix = rawPrefix.toUpperCase();

    if (prefix === "TX") {
      return suffix;
    }

    if (isValidStateWasteCode(suffix, prefix)) {
      return suffix;
    }

    return trimmed;
  }

  const noDashMatch = trimmed.match(NO_DASH_STATE_PREFIX_REGEX);
  if (noDashMatch) {
    const [, rawPrefix, rawSuffix] = noDashMatch;
    const prefix = rawPrefix.toUpperCase();
    // Gate on STATE_WASTE_CODES membership so facility-specific TX (not a key
    // here) and declared-without-entries LA/PA/WI never trigger the no-dash
    // strip. Without this guard, isValidStateWasteCode would accept any TX
    // suffix and we'd slice the first two characters off arbitrary inputs.
    if (STATE_WASTE_CODES[prefix] !== undefined && isValidStateWasteCode(rawSuffix, prefix)) {
      return rawSuffix;
    }
  }

  return trimmed;
}

/**
 * TX hazardous waste codes are a 7-digit sequence followed by an "H"
 * classification character (TCEQ RG-22, Aug 2025), e.g. "0001001H". An
 * optional dash between the digits and the H is tolerated because that
 * form appears in vendor pipeline data. Letter-sequence H codes (UNIVxxxH,
 * PHRMxxxH) are not hazardous.
 */
const TX_TCEQ_HAZARDOUS_PATTERN = /^\d{7}-?H$/;

const TX_DASH_PREFIX_REGEX = /^\s*TX-/i;

/**
 * Whether some state recognizes an uppercase state waste code, judged with any
 * state prefix dropped ("OR-F999" as "F999"). Texas keeps no code list, so its
 * codes are judged by the format rule the compliance engine runs. A code written
 * "TX-…" is a Texas code, so it is judged only by that rule: the normalizer
 * strips "TX-" unconditionally, and "TX-121" must not pass as California's "121".
 */
export function isRecognizedStateWasteCode(code: string): boolean {
  const normalized = normalizeStateWasteCode(code) ?? code;
  if (TX_DASH_PREFIX_REGEX.test(code)) return isValidTxWasteCodeFormat(normalized);
  return isValidStateWasteCodeInAnyState(normalized) || isValidTxWasteCodeFormat(normalized);
}

export function isTxTceqHazardousCode(code: string | null | undefined): boolean {
  const normalized = normalizeStateWasteCode(code) ?? code;
  if (normalized === null || normalized === undefined) {
    return false;
  }
  return TX_TCEQ_HAZARDOUS_PATTERN.test(normalized.trim().toUpperCase());
}

/**
 * Normalize a list of state waste codes: normalize each entry, drop nulls,
 * dedupe, and sort alphabetically for deterministic output.
 */
export function normalizeStateWasteCodes(
  codes: readonly (string | null | undefined)[] | null | undefined
): string[] {
  if (codes === null || codes === undefined) {
    return [];
  }

  const canonical: string[] = [];
  for (const code of codes) {
    const normalized = normalizeStateWasteCode(code);
    if (normalized !== null) {
      canonical.push(normalized);
    }
  }

  return Array.from(new Set(canonical)).sort();
}
